import { MainLayout } from '@/components/layout/MainLayout';
import { DetailTreeNav, type TreeNode } from '@/components/layout/DetailTreeNav';
import { JobsListPanel } from '@/components/jobs/JobsListPanel';
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getJobById, getJobFinancials, createScheduledJob, updateJobStatus, type JobScheduleData, type JobFinancials } from '@/lib/api/jobs';
import { useAuth } from '@/lib/hooks/useAuth';
import apiClient from '@/lib/api/client';
import { ClientInfoCard } from '@/components/jobs/ClientInfoCard';
import { SiteAddressCard } from '@/components/jobs/SiteAddressCard';
import { KeyDatesCard } from '@/components/jobs/KeyDatesCard';
import { ProjectManagerCard } from '@/components/jobs/ProjectManagerCard';
import { FinancialSummaryCard } from '@/components/jobs/FinancialSummaryCard';
import { DocumentsSection } from '@/components/jobs/DocumentsSection';
import { ScheduleTab } from '@/components/jobs/ScheduleTab';
import { JobCrewPanel } from '@/components/jobs/JobCrewPanel';
import { JobNotesList } from '@/components/jobs/JobNotesList';
import { JobMaterialsList } from '@/components/jobs/JobMaterialsList';
import { AddMaterialModal } from '@/components/jobs/AddMaterialModal';
import { JobStatusBadge, JobStatusSelector } from '@/components/jobs/JobStatusBadge';
import { JobEditModal } from '@/components/jobs/JobEditModal';
import { JobSchedulingForm } from '@/components/jobs/JobSchedulingForm';
import { JobTasksTab } from '@/components/jobs/JobTasksTab';
import { EstimatesTab } from '@/components/jobs/EstimatesTab';
import { MaterialEstimatesTab } from '@/components/jobs/MaterialEstimatesTab';
import { InvoicesTab } from '@/components/jobs/InvoicesTab';
import { JobExpensesTab } from '@/components/jobs/JobExpensesTab';
import { ChangeOrdersTab } from '@/components/jobs/ChangeOrdersTab';
import { PhotoGalleryTab } from '@/components/jobs/PhotoGalleryTab';
import { DailyLogsTab } from '@/components/jobs/DailyLogsTab';
import { RFIsTab } from '@/components/jobs/RFIsTab';
import { PermitsTab } from '@/components/jobs/PermitsTab';
import { ContractBuilderTab } from '@/components/jobs/ContractBuilderTab';
import { AgreementsTab } from '@/components/jobs/AgreementsTab';
import { DetailSection, LargeTitleHeader } from '@/components/common';
import { exportJobToPdf } from '@/lib/utils/generateJobPdf';
import { trackRecentJob } from '@/pages/dashboard/DashboardPage';
import { ToastContainer } from '@/components/common/Toast';
import { useToast } from '@/lib/hooks/useToast';

interface Job {
  id?: string;
  jobNumber: string;
  title: string;
  status: string;
  client: {
    name: string;
    email: string;
    phone: string;
  };
  clientId?: number;
  address: string;
  city?: string;
  province?: string;
  postalCode?: string;
  parkingInstructions?: string;
  startDate: string;
  estimatedEndDate: string;
  actualEndDate?: string;
  projectManager: {
    name: string;
    email: string;
    phone: string;
    avatar?: string;
  };
  budget: number;
  spent: number;
}

export const JobDetailPage = () => {
  // Supports both the flat route (/jobs/:id) and the canonical nested route
  // (/clients/:clientId/jobs/:jobId/:section?). Once the job loads on the flat route,
  // we upgrade the URL to the nested form so the address bar mirrors the hierarchy.
  const { id: flatId, clientId: clientIdParam, jobId, section } = useParams<{
    id?: string; clientId?: string; jobId?: string; section?: string;
  }>();
  const id = jobId || flatId;
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [job, setJob] = useState<Job | null>(null);
  // The visible section is URL-driven (deep-linkable); default to overview.
  const activeSection = section || 'overview';
  const setActiveSection = (key: string) => {
    const cid = clientIdParam || job?.clientId;
    navigate(cid ? `/clients/${cid}/jobs/${id}/${key}` : `/jobs/${id}`);
  };
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [creatingJob, setCreatingJob] = useState(false);
  const [financials, setFinancials] = useState<JobFinancials | null>(null);
  const [generatingCloseout, setGeneratingCloseout] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const invoicesRef = useRef<any>(null);
  const { toasts, removeToast, toast } = useToast();

  useEffect(() => {
    loadJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Upgrade the flat /jobs/:id URL to the canonical /clients/:c/jobs/:j once the job
  // (and thus its client) is known, so the address bar mirrors the hierarchy.
  useEffect(() => {
    if (!job?.clientId) return;
    if (String(clientIdParam) !== String(job.clientId)) {
      navigate(`/clients/${job.clientId}/jobs/${id}${section ? '/' + section : ''}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, clientIdParam, id, section]);

  const loadJob = async () => {
    try {
      setLoading(true);
      if (!id) return;

      const [jobData, jobFinancials] = await Promise.all([
        getJobById(id),
        getJobFinancials(id).catch(() => null),
      ]);

      // Transform API response to match component interface
      // Track recently-viewed job (#17)
      trackRecentJob({
        id: Number(jobData.id),
        jobNumber: jobData.job_number as string,
        title: jobData.title as string,
        status: jobData.status as string,
      });

      setJob({
        id: jobData.id as string,
        jobNumber: jobData.job_number as string,
        title: jobData.title as string,
        status: jobData.status as string,
        clientId: jobData.client_id as number,
        client: {
          name: jobData.client_name as string || 'N/A',
          email: jobData.client_email as string || '',
          phone: jobData.client_phone as string || '',
        },
        address: jobData.site_address as string || '',
        city: jobData.site_city as string || '',
        province: jobData.site_province as string || '',
        postalCode: jobData.site_postal_code as string || '',
        parkingInstructions: jobData.parking_instructions as string || '',
        startDate: jobData.start_date as string || '',
        estimatedEndDate: jobData.end_date as string || '',
        actualEndDate: jobData.actual_end_date as string,
        projectManager: {
          name: jobData.project_manager as string || 'Unassigned',
          email: (jobData.pm_email as string) || '',
          phone: (jobData.pm_phone as string) || '',
        },
        budget: parseFloat(jobData.budget as string) || 0,
        spent: parseFloat(jobData.actual_cost as string) || 0,
      });
      setFinancials(jobFinancials);
    } catch (error) {
      console.error('Failed to load job:', error);
      setLoadError('Failed to load job details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseoutDownload = async () => {
    if (!id) return;
    try {
      setGeneratingCloseout(true);
      const response = await apiClient.get(`/jobs/${id}/closeout`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `closeout-job${id}-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Closeout download failed:', error);
      toast.error('Failed to generate closeout package.');
    } finally {
      setGeneratingCloseout(false);
    }
  };

  const handleNewJobSubmit = async (formData: JobScheduleData) => {
    try {
      setCreatingJob(true);
      await createScheduledJob(formData);
      setShowNewJobModal(false);
      navigate('/jobs');
    } catch (error) {
      console.error('Failed to create job:', error);
      toast.error('Failed to create job. Please try again.');
      setCreatingJob(false);
    }
  };

  if (loading) {
    return (
      <MainLayout clientListPanel={<JobsListPanel />}>
        <div className="space-y-4">
          <div className="h-10 bg-gray-100 rounded-xl animate-pulse w-1/2" />
          <div className="h-5 bg-gray-100 rounded animate-pulse w-1/4" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-32 bg-gray-100 rounded-xl animate-pulse" />)}
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!job) {
    return (
      <MainLayout clientListPanel={<JobsListPanel />}>
        <div className="flex flex-col items-center justify-center h-64 gap-3">
          <span className="material-symbols-outlined text-gray-300 text-5xl">{loadError ? 'error' : 'work_off'}</span>
          <p className="text-gray-500 font-medium">{loadError || 'Job not found'}</p>
          {loadError ? (
            <button onClick={loadJob} className="text-sm font-semibold text-primary border border-primary/30 rounded-lg px-4 py-2 hover:bg-primary/5">
              Retry
            </button>
          ) : (
            <p className="text-gray-400 text-sm">This job may have been deleted or the link is incorrect.</p>
          )}
        </div>
      </MainLayout>
    );
  }

  const jobTreeNodes: TreeNode[] = [
    { key: 'overview',     label: 'Overview',           icon: 'dashboard' },
    { key: 'documents',    label: 'Docs & Photos',      icon: 'folder' },
    { key: 'notes',        label: 'Notes',              icon: 'note' },
    { key: 'schedule',     label: 'Schedule',           icon: 'calendar_month' },
    { key: 'crew',         label: 'Crew',               icon: 'groups' },
    { key: 'tasks',        label: 'Tasks',              icon: 'task_alt' },
    { key: 'materials',    label: 'Materials Used',     icon: 'inventory_2' },
    { key: 'estimates',    label: 'Estimates & Quotes', icon: 'request_quote' },
    { key: 'mlestimates',  label: 'Material & Labour',  icon: 'calculate' },
    { key: 'invoices',     label: 'Invoices',           icon: 'receipt_long' },
    { key: 'expenses',     label: 'Expenses',           icon: 'receipt' },
    { key: 'changeorders', label: 'Change Orders',      icon: 'edit_note' },
    { key: 'photos',       label: 'Photo Gallery',      icon: 'photo_library' },
    { key: 'logs',         label: 'Daily Logs',         icon: 'description' },
    { key: 'rfis',         label: 'RFIs',               icon: 'help_outline' },
    { key: 'permits',      label: 'Permits',            icon: 'fact_check' },
    { key: 'agreements',   label: 'Agreements',         icon: 'handshake' },
    { key: 'contract',     label: 'Contract Builder',   icon: 'history_edu' },
  ];

  return (
    <MainLayout
      clientListPanel={
        <DetailTreeNav
          title={job.title}
          subtitle={`${job.jobNumber} · ${job.status}`}
          back={{ label: 'All jobs', onClick: () => navigate('/jobs') }}
          nodes={jobTreeNodes}
          activeKey={activeSection}
          onSelect={setActiveSection}
        />
      }
      noPadding
    >
      <div className="px-1.5 py-3 md:p-6">
        {/* iOS Large Title Header */}
        <LargeTitleHeader
          title={job.title}
          subtitle={`${job.jobNumber} · ${job.status}`}
          onBack={() => navigate('/jobs')}
          backLabel="Jobs"
          actions={
            <div className="flex gap-1.5">
              <button
                onClick={() => setShowStatusModal(true)}
                className="flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 bg-gray-50 hover:bg-primary/10 transition-colors"
                title="Change status"
              >
                <span className="material-symbols-outlined text-lg text-gray-700">flag</span>
              </button>
              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center gap-1.5 cursor-pointer overflow-hidden rounded-lg h-9 px-3 bg-primary text-white text-sm font-bold hover:opacity-90 transition-opacity"
              >
                <span className="material-symbols-outlined text-base">edit</span>
                <span className="hidden sm:inline truncate">Edit</span>
              </button>
              {job.status === 'completed' && (
                <button
                  onClick={handleCloseoutDownload}
                  disabled={generatingCloseout}
                  className="flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 bg-gray-50 hover:bg-primary/10 transition-colors disabled:opacity-60"
                  title={generatingCloseout ? 'Generating…' : 'Closeout Package'}
                >
                  <span className="material-symbols-outlined text-lg text-gray-700">archive</span>
                </button>
              )}
              <button
                onClick={async () => {
                  setExportingPdf(true);
                  try {
                    await exportJobToPdf(job);
                  } catch {
                    toast.error('Failed to generate PDF.');
                  } finally {
                    setExportingPdf(false);
                  }
                }}
                disabled={exportingPdf}
                className="flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 bg-gray-50 hover:bg-primary/10 transition-colors disabled:opacity-60"
                title={exportingPdf ? 'Exporting…' : 'Export to PDF'}
              >
                <span className="material-symbols-outlined text-lg text-gray-700">picture_as_pdf</span>
              </button>
            </div>
          }
        >
          {/* Status badge shown beneath the large title */}
          <div className="flex items-center gap-2 pb-1">
            <JobStatusBadge status={job.status} onClick={() => setShowStatusModal(true)} />
            <span className="text-sm text-gray-500">{job.jobNumber}</span>
          </div>
        </LargeTitleHeader>

        {/* Collapsible Sections */}
        <div className="space-y-4">
          {/* Overview Section — compact info density */}
          <DetailSection title="Project Overview" icon="dashboard" sectionKey="overview" activeKey={activeSection} onActivate={setActiveSection}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {/* Information Cards */}
              <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                <ClientInfoCard client={job.client} />
                <SiteAddressCard 
                  address={job.address}
                  city={job.city}
                  province={job.province}
                  postalCode={job.postalCode}
                  parkingInstructions={job.parkingInstructions}
                />
                <KeyDatesCard
                  startDate={job.startDate}
                  estimatedEndDate={job.estimatedEndDate}
                  actualEndDate={job.actualEndDate}
                />
                <ProjectManagerCard manager={job.projectManager} />
              </div>

              {/* Financial Summary */}
              <FinancialSummaryCard
                budget={job.budget}
                spent={job.spent}
                revisedContractValue={financials?.revisedContractValue}
                approvedChangeOrderTotal={financials?.approvedChangeOrderTotal}
                approvedChangeOrderCount={financials?.approvedChangeOrderCount}
                remaining={financials?.remaining}
                percentageUsed={financials?.percentageUsed}
                profitMargin={financials?.profitMargin}
                expensesTotal={financials?.expensesTotal}
                labourCost={financials?.labourCost}
                labourHours={financials?.labourHours}
              />
            </div>

          </DetailSection>

          {/* Documents & Photos — its own collapsible section (CR #79) */}
          {id && (
            <DetailSection title="Docs & Photos" icon="folder" sectionKey="documents" activeKey={activeSection} onActivate={setActiveSection}>
              <DocumentsSection jobId={id} />
            </DetailSection>
          )}

          {/* Notes Section */}
          {id && (
            <DetailSection title="Job Notes" icon="note" sectionKey="notes" activeKey={activeSection} onActivate={setActiveSection}>
              <JobNotesList jobId={id} />
            </DetailSection>
          )}

          {/* Schedule Section */}
          {id && job && (
            <DetailSection title="Schedule" icon="calendar_month" sectionKey="schedule" activeKey={activeSection} onActivate={setActiveSection}>
              <ScheduleTab 
                jobId={id}
                jobDetails={{
                  startDate: job.startDate,
                  endDate: job.estimatedEndDate,
                  title: job.title,
                  jobNumber: job.jobNumber,
                  status: job.status
                }}
              />
            </DetailSection>
          )}

          {/* Crew Section — who's attributed to this job (drives worker portals) */}
          {id && (
            <DetailSection title="Crew" icon="groups" sectionKey="crew" activeKey={activeSection} onActivate={setActiveSection}>
              <JobCrewPanel jobId={id} />
            </DetailSection>
          )}

          {/* Tasks Section */}
          {id && (
            <DetailSection title="Tasks" icon="task_alt" sectionKey="tasks" activeKey={activeSection} onActivate={setActiveSection}>
              <JobTasksTab jobId={id} />
            </DetailSection>
          )}

          {/* Materials Section */}
          {id && (
            <DetailSection title="Materials Used" icon="inventory_2" sectionKey="materials" activeKey={activeSection} onActivate={setActiveSection}>
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => setShowMaterialModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
                >
                  <span className="material-symbols-outlined text-sm">add</span>
                  Add Material
                </button>
              </div>
              <JobMaterialsList jobId={id} />
            </DetailSection>
          )}

          {/* Estimates & Quotes Section */}
          {id && (
            <DetailSection title="Estimates & Quotes" icon="request_quote" sectionKey="estimates" activeKey={activeSection} onActivate={setActiveSection}>
              <EstimatesTab
                jobId={id}
                clientId={job?.clientId}
                jobTitle={job?.title}
                siteAddress={[job?.address, job?.city, job?.province].filter(Boolean).join(', ')}
              />
            </DetailSection>
          )}

          {/* Material & Labour Estimates Section */}
          {id && (
            <DetailSection title="Material & Labour Estimates" icon="calculate" sectionKey="mlestimates" activeKey={activeSection} onActivate={setActiveSection}>
              <MaterialEstimatesTab jobId={id} />
            </DetailSection>
          )}

          {/* Invoices Section */}
          {id && (
            <DetailSection
              title="Invoices"
              icon="receipt_long"
              sectionKey="invoices"
              activeKey={activeSection}
              onActivate={setActiveSection}
              headerActions={
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    invoicesRef.current?.openCreateInvoice?.();
                  }}
                  className="flex items-center gap-2 cursor-pointer overflow-hidden rounded-lg h-8 px-3 bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
                >
                  <span className="material-symbols-outlined text-base">add</span>
                  <span className="truncate">Create Invoice</span>
                </button>
              }
            >
              <InvoicesTab ref={invoicesRef} jobId={id} clientId={job?.clientId || 0} />
            </DetailSection>
          )}

          {/* Expenses Section — scan receipts, AI-categorized spend-to-date */}
          {id && (
            <DetailSection title="Expenses" icon="receipt" sectionKey="expenses" activeKey={activeSection} onActivate={setActiveSection}>
              <JobExpensesTab jobId={id} />
            </DetailSection>
          )}

          {/* Change Orders Section */}
          {id && (
            <DetailSection title="Change Orders" icon="edit_note" sectionKey="changeorders" activeKey={activeSection} onActivate={setActiveSection}>
              <ChangeOrdersTab jobId={parseInt(id)} />
            </DetailSection>
          )}

          {/* Photo Gallery Section */}
          {id && (
            <DetailSection title="Photo Gallery" icon="photo_library" sectionKey="photos" activeKey={activeSection} onActivate={setActiveSection}>
              <PhotoGalleryTab jobId={parseInt(id)} />
            </DetailSection>
          )}

          {/* Daily Logs Section */}
          {id && (
            <DetailSection title="Daily Logs" icon="edit_note" sectionKey="logs" activeKey={activeSection} onActivate={setActiveSection}>
              <DailyLogsTab jobId={id} />
            </DetailSection>
          )}

          {id && (
            <DetailSection title="RFIs" icon="help_outline" sectionKey="rfis" activeKey={activeSection} onActivate={setActiveSection}>
              <RFIsTab jobId={id} />
            </DetailSection>
          )}

          {id && (
            <DetailSection title="Permits & Inspections" icon="fact_check" sectionKey="permits" activeKey={activeSection} onActivate={setActiveSection}>
              <PermitsTab jobId={id} />
            </DetailSection>
          )}

          {/* Agreements — job-scoped work agreements / work orders (CR #199) */}
          {id && (
            <DetailSection title="Agreements" icon="handshake" sectionKey="agreements" activeKey={activeSection} onActivate={setActiveSection}>
              <AgreementsTab jobId={id} clientId={job?.clientId} />
            </DetailSection>
          )}

          {id && job && (
            <DetailSection title="Contract Builder" icon="description" sectionKey="contract" activeKey={activeSection} onActivate={setActiveSection}>
              <ContractBuilderTab
                jobId={id}
                jobTitle={job.title}
                jobNumber={job.jobNumber}
                clientName={job.client.name}
                siteAddress={job.address}
                startDate={job.startDate}
                endDate={job.estimatedEndDate}
                budget={job.budget}
              />
            </DetailSection>
          )}
        </div>

        {/* Modals */}
        {showStatusModal && (
          <JobStatusSelector
            currentStatus={job.status}
            onStatusChange={async (status, notes) => {
              try {
                await updateJobStatus(id!, status, authUser?.id.toString(), notes);
                await loadJob();
                setShowStatusModal(false);
              } catch (error) {
                console.error('Failed to update status:', error);
                toast.error('Failed to update job status.');
              }
            }}
            onClose={() => setShowStatusModal(false)}
          />
        )}

        {showMaterialModal && id && (
          <AddMaterialModal
            jobId={id}
            onClose={() => setShowMaterialModal(false)}
            onSuccess={loadJob}
          />
        )}

        {showEditModal && id && (
          <JobEditModal
            jobId={id}
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            onSaved={() => {
              loadJob();
              setShowEditModal(false);
            }}
          />
        )}

        {/* New Job Modal */}
        <JobSchedulingForm
          isOpen={showNewJobModal}
          onClose={() => setShowNewJobModal(false)}
          onSubmit={handleNewJobSubmit}
          isSubmitting={creatingJob}
        />
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </MainLayout>
  );
};

